/* eslint-disable @typescript-eslint/indent */
import { FieldValues } from 'react-hook-form';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isDefined, isNotDefined } from '@hsp/utilities';

import { ConvertState } from 'store/audience-builder/utilities';
import { DataPointControlType, defaultConditions, IFormCategory, IMultiRfmGroup } from 'types/api/audience';
import { SearchType } from 'types/api/audience/enums';
import { JoinType } from 'types/api/common';
import { ApplicationState } from 'types/store';
import {
  ConversionStatus,
  IAudienceBuilderSharedState,
  IAudienceBuilderSharedUsedDataPointState,
} from 'types/store/audience-builder';

const initialState: IAudienceBuilderSharedState = {
  conversionStatus: 'not-started',
  dataSourceType: 'house',
  defaultFieldValues: {},
  fieldValues: {},
  filterType: SearchType.DataSource,
  isMultiRfm: false,
  multiRfmGroups: [],
  multiRfmIndex: undefined,
  multiRfmOperation: undefined,
  previousFieldValues: {},
  previousSearchType: SearchType.Basic,
  recencyType: 'date-range',
  searchType: SearchType.DataSource,
  selectedCategory: SearchType.DataSource,
  selectedSearch: SearchType.Basic,
  usedDataPoints: {
    and: [],
    or: [],
  },
};

export const convert = createAsyncThunk('ab/convert', async (categories: IFormCategory[], { getState }) => {
  return new Promise<FieldValues>((resolve, reject) => {
    const state = (getState() as ApplicationState).audience.shared;
    const { previousSearchType, searchType, selectedSearch } = state;
    let { fieldValues } = state;

    if (previousSearchType === searchType) {
      resolve(fieldValues);

      return;
    }

    try {
      const converter = new ConvertState({ ...fieldValues }, categories);
      let converted: FieldValues = {};

      if (previousSearchType === SearchType.Basic) {
        switch (selectedSearch) {
          case SearchType.Conditions:
            converted = converter.toConditions(SearchType.Basic);
            fieldValues = {
              ...fieldValues,
              ...converted,
              [SearchType.Basic]: categories.reduce((previous, { category }) => {
                return {
                  ...previous,
                  [category]: {},
                };
              }, {}),
            };
            break;

          case SearchType.Sequences:
            break;
        }
      } else if (previousSearchType === SearchType.Conditions) {
        switch (selectedSearch) {
          case SearchType.Basic:
            converted = converter.toBasic(SearchType.Conditions);
            fieldValues = {
              ...fieldValues,
              ...converted,
              [SearchType.Conditions]: defaultConditions,
            };
            break;

          case SearchType.Sequences:
            break;
        }
      } else if (previousSearchType === SearchType.Sequences) {
        switch (selectedSearch) {
          case SearchType.Basic:
            break;

          case SearchType.Conditions:
            break;
        }
      }

      resolve(fieldValues);
    } catch (e) {
      reject(e);
    }
  });
});

export const sharedSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(convert.fulfilled, (state, { payload }) => {
      return {
        ...state,
        conversionStatus: 'finished',
        fieldValues: {
          ...state.fieldValues,
          ...payload,
        },
        forceUpdate: false,
        reset: false,
        submit: false,
        usedDataPoints: {
          and: [],
          or: [],
        },
      };
    });
    builder.addCase(convert.pending, (state) => {
      state.conversionStatus = 'started';
    });
    builder.addCase(convert.rejected, (state) => {
      state.conversionError = null;
      state.conversionStatus = 'error';
    });
  },
  initialState,
  name: 'ab/shared',
  reducers: {
    addMultiRfmGroup: (state, { payload }: PayloadAction<IMultiRfmGroup>) => {
      state.multiRfmGroups = [...state.multiRfmGroups, payload];
      state.multiRfmIndex = state.multiRfmGroups.length - 1;
    },
    addUsedDataPointAnd: (state, { payload }: PayloadAction<string>) => {
      state.usedDataPoints.and.push(payload);
    },
    addUsedDataPointOr: (state, { payload }: PayloadAction<string>) => {
      state.usedDataPoints.or.push(payload);
    },
    clear: (state, { payload }: PayloadAction<(keyof IAudienceBuilderSharedState)[] | undefined>) => {
      const { defaultFieldValues } = state;
      const keep: Record<string, unknown> = {};

      if (isDefined(payload)) {
        for (const key of payload) {
          keep[key] = { ...state }[key];
        }
      }

      return {
        ...initialState,
        ...keep,
        defaultFieldValues: { ...defaultFieldValues },
        fieldValues: { ...defaultFieldValues },
        previousFieldValues: { ...defaultFieldValues },
      };
    },
    clearMultiRfmGroups: (state) => {
      state.multiRfmGroups = [];
      delete state.multiRfmIndex;
      delete state.multiRfmOperation;
    },
    removeMultiRfmGroup: (state, { payload }: PayloadAction<number>) => {
      state.multiRfmGroups = [...state.multiRfmGroups.slice(0, payload), ...state.multiRfmGroups.slice(payload + 1)];
    },
    removeUsedDataPointAnd: (state, { payload }: PayloadAction<string>) => {
      const {
        usedDataPoints: { and },
      } = state;
      const index = and.findIndex((used) => used === payload);

      if (index >= 0) {
        state.usedDataPoints.and.splice(index, 1);
      }
    },
    removeUsedDataPointOr: (state, { payload }: PayloadAction<string>) => {
      const {
        usedDataPoints: { or },
      } = state;
      const index = or.findIndex((used) => used === payload);

      if (index >= 0) {
        state.usedDataPoints.or.splice(index, 1);
      }
    },
    setAllFieldValues: (state, { payload }: PayloadAction<FieldValues>) => {
      state.defaultFieldValues = payload;
      state.fieldValues = payload;
      state.previousFieldValues = payload;
    },
    setConversionStatus: (state, { payload }: PayloadAction<ConversionStatus>) => {
      state.conversionStatus = payload;
    },
    setDataSourceType: (state, { payload }: PayloadAction<string>) => {
      state.dataSourceType = payload;
    },
    setFieldValues: (state, { payload }: PayloadAction<FieldValues>) => {
      const { fieldValues } = state;

      state.previousFieldValues = { ...fieldValues };
      state.fieldValues = { ...payload };
    },
    setFilterType: (state, { payload }: PayloadAction<SearchType>) => {
      state.filterType = payload;
    },
    setIsMultiRfm: (state, { payload }: PayloadAction<boolean>) => {
      state.isMultiRfm = payload;
    },
    setMultiRfmGroups: (state, { payload }: PayloadAction<IMultiRfmGroup[]>) => {
      state.multiRfmGroups = payload;
    },
    setMultiRfmIndex: (state, { payload }: PayloadAction<number>) => {
      state.multiRfmIndex = payload;
    },
    setMultiRfmOperation: (state, { payload }: PayloadAction<JoinType | undefined>) => {
      state.multiRfmOperation = payload;
    },
    setPreviousFieldValues: (state, { payload }: PayloadAction<FieldValues>) => {
      state.previousFieldValues = { ...state.defaultFieldValues, ...payload };
    },
    setRecencyType: (state, { payload }: PayloadAction<DataPointControlType>) => {
      state.recencyType = payload;
    },
    setSearchType: (state, { payload }: PayloadAction<SearchType>) => {
      state.searchType = payload;
    },
    setSelectedCategory: (state, { payload }: PayloadAction<string>) => {
      state.selectedCategory = payload;
    },
    setSelectedDataSourceType: (state, { payload }: PayloadAction<string>) => {
      state.dataSourceType = payload;
    },
    setSelectedSearch: (state, { payload }: PayloadAction<SearchType>) => {
      state.previousSearchType = state.selectedSearch;
      state.selectedSearch = payload;
    },
    setUsedDataPoint: (state, { payload }: PayloadAction<IAudienceBuilderSharedUsedDataPointState>) => {
      state.usedDataPoints = payload;
    },
    updateMultiRfmGroup: (state, { payload }: PayloadAction<{ group: IMultiRfmGroup; index: number | undefined }>) => {
      const { group, index } = payload;

      if (isNotDefined(index) || index < 0 || index > state.multiRfmGroups.length - 1) {
        return;
      }

      state.multiRfmGroups[index] = { ...group };
    },
    updateMultiRfmGroupFieldValues: (
      state,
      { payload }: PayloadAction<{ fieldValues: FieldValues; index: number | undefined }>
    ) => {
      const { fieldValues, index } = payload;

      if (isNotDefined(index) || index < 0 || index > state.multiRfmGroups.length - 1) {
        return;
      }

      const current = { ...state.multiRfmGroups[index] };

      state.multiRfmGroups[index] = { ...current, ...{ fieldValues } };
    },
    updateMultiRfmGroupName: (
      state,
      { payload }: PayloadAction<{ index: number | undefined; name: string | undefined }>
    ) => {
      const { name, index } = payload;

      if (isNotDefined(index) || index < 0 || index > state.multiRfmGroups.length - 1) {
        return;
      }

      const current = { ...state.multiRfmGroups[index] };

      state.multiRfmGroups[index] = { ...current, name };
    },
    updateMultiRfmGroups: (state, { payload }: PayloadAction<IMultiRfmGroup[]>) => {
      state.multiRfmGroups = payload;
    },
  },
});

export const sharedReducer = sharedSlice.reducer;
